/**
 * Verbindung zum Server. Einzige Stelle, die URLs und Tokens kennt –
 * wer das Frontend umbaut, benutzt einfach diese Funktionen weiter.
 */
import { API_BASE, ROUTES } from '@pflanzen/shared';
import { get, set, del } from 'idb-keyval';

const TOKEN_KEY = 'pflanzen-device-token';
let tokenCache: string | null | undefined;

export async function getToken(): Promise<string | null> {
  if (tokenCache !== undefined) return tokenCache;
  tokenCache = (await get<string>(TOKEN_KEY)) ?? localStorage.getItem(TOKEN_KEY);
  return tokenCache;
}

export async function setToken(token: string | null): Promise<void> {
  tokenCache = token;
  if (token) {
    await set(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    await del(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
  /** Kein Netz / Server nicht erreichbar. */
  get offline(): boolean {
    return this.status === 0;
  }
}

/** Pfad aus ROUTES mit :platzhaltern füllen, z. B. path('plant', { id }). */
export function path(route: keyof typeof ROUTES, params: Record<string, string> = {}): string {
  let p: string = ROUTES[route];
  for (const [k, v] of Object.entries(params)) p = p.replace(`:${k}`, encodeURIComponent(v));
  return API_BASE + p;
}

export async function api<T>(method: string, url: string, body?: unknown, opts: { raw?: boolean; timeoutMs?: number } = {}): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 20_000);
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body: payload, signal: ctrl.signal });
  } catch {
    throw new ApiError(0, 'offline', 'Keine Verbindung zum Server.');
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) {
    let code = 'error';
    let message = `Fehler ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string; message?: string };
      code = j.error ?? code;
      message = j.message ?? message;
    } catch {
      /* kein JSON */
    }
    throw new ApiError(res.status, code, message);
  }
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
